import type { FC } from 'react';

import type { ResolutionType } from '@/constants/videoModels';

import AlibabaSvg from '~/svg/alibaba.svg';
import ByteDanceSvg from '~/svg/bytedance.svg';
import GoogleSvg from '~/svg/google_small.svg';
import HailuoSvg from '~/svg/Hailuo.svg';
import KlingSvg from '~/svg/Kling.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import XAISvg from '~/svg/xai.svg';

export interface VideoGenerationPreset {
  id: string;
  label: string;
  description: string;
  /** Appended to each scene's visual prompt */
  stylePrompt: string;
  /** Thumbnail/preview image path */
  thumbnail?: string;
}

export const VIDEO_GENERATION_PRESETS: VideoGenerationPreset[] = [
  {
    id: 'default',
    label: 'Default',
    description: 'Realistic, high-quality visuals',
    stylePrompt: '',
    thumbnail: '/images/video-styles/default.webp',
  },
  {
    id: 'cinematic',
    label: 'Cinematic',
    description: 'Film-like dramatic lighting and composition',
    stylePrompt:
      'cinematic film still, dramatic lighting, shallow depth of field, anamorphic lens, 35mm film grain, color grading',
    thumbnail: '/images/video-styles/cinematic.webp',
  },
  {
    id: 'ghibli',
    label: 'Ghibli Studio',
    description: 'Whimsical anime watercolor style',
    stylePrompt:
      'Studio Ghibli anime style, watercolor backgrounds, whimsical atmosphere, hand-drawn aesthetic, soft pastel colors',
    thumbnail: '/images/video-styles/ghibli.webp',
  },
  {
    id: 'educational',
    label: 'Educational',
    description: 'Clean infographic style with diagrams',
    stylePrompt:
      'clean educational infographic style, flat design, clear labels and annotations, professional diagram, white background with accent colors',
    thumbnail: '/images/video-styles/educational.webp',
  },
  {
    id: 'pixar',
    label: 'Pixar',
    description: '3D animation with vibrant characters',
    stylePrompt:
      '3D Pixar-style animation, vibrant saturated colors, expressive characters, smooth rendering, playful and polished',
    thumbnail: '/images/video-styles/pixar.webp',
  },
  {
    id: 'anime',
    label: 'Anime',
    description: 'Japanese anime style art',
    stylePrompt:
      'anime style, detailed character art, vibrant colors, dynamic composition, manga-inspired shading',
    thumbnail: '/images/video-styles/anime.webp',
  },
  {
    id: 'watercolor',
    label: 'Watercolor',
    description: 'Soft watercolor painting aesthetic',
    stylePrompt:
      'watercolor painting, soft blended edges, artistic texture, muted tones, delicate brush strokes',
    thumbnail: '/images/video-styles/watercolor.webp',
  },
  {
    id: 'comic',
    label: 'Comic Book',
    description: 'Bold comic book illustration',
    stylePrompt:
      'comic book illustration, bold outlines, halftone shading, dynamic action poses, vibrant primary colors, speech bubble aesthetic',
    thumbnail: '/images/video-styles/comic.webp',
  },
  {
    id: 'minimalist',
    label: 'Minimalist',
    description: 'Clean and simple modern design',
    stylePrompt:
      'minimalist design, simple shapes, flat illustration, limited color palette, clean composition, modern and elegant',
    thumbnail: '/images/video-styles/minimalist.webp',
  },
  {
    id: 'retro',
    label: 'Retro',
    description: '80s/90s retro aesthetic',
    stylePrompt:
      'retro 80s aesthetic, neon colors, synthwave style, geometric shapes, vintage filter, nostalgic vibe',
    thumbnail: '/images/video-styles/retro.webp',
  },
  {
    id: 'flat-animation',
    label: 'Flat Animation',
    description: 'Clean 2D animation with simple shapes',
    stylePrompt:
      'flat 2D animation style, clean vector shapes, simple shading, smooth character design, bright friendly color palette',
    thumbnail: '/images/video-styles/flat-animation.webp',
  },
  {
    id: 'sketch-color',
    label: 'Sketch Color',
    description: 'Loose hand-drawn sketch with color accents',
    stylePrompt:
      'colored pencil sketch, visible hand-drawn linework, loose shading, textured paper, natural color accents',
    thumbnail: '/images/video-styles/sketch-color.webp',
  },
  {
    id: 'sketch-black-white',
    label: 'Sketch B&W',
    description: 'Black and white pencil sketch style',
    stylePrompt:
      'black and white pencil sketch, graphite linework, tonal hatching, paper texture, monochrome hand-drawn look',
    thumbnail: '/images/video-styles/sketch-black-white.webp',
  },
  {
    id: 'japanese-ink',
    label: 'Japanese Ink',
    description: 'Expressive ink wash illustration',
    stylePrompt:
      'Japanese ink wash illustration, sumi-e brushwork, rice paper texture, expressive black ink, muted wash tones',
    thumbnail: '/images/video-styles/japanese-ink.webp',
  },
  {
    id: '3d',
    label: '3D',
    description: 'Polished 3D render style',
    stylePrompt:
      'polished 3D render, cinematic lighting, detailed materials, smooth character model, realistic depth of field',
    thumbnail: '/images/video-styles/3d.webp',
  },
  {
    id: 'low-poly',
    label: 'Low Poly',
    description: 'Faceted low-poly 3D art',
    stylePrompt:
      'low-poly 3D art, faceted geometry, simplified forms, crisp lighting, stylized game-art aesthetic',
    thumbnail: '/images/video-styles/low-poly.webp',
  },
  {
    id: 'lego',
    label: 'Lego',
    description: 'Toy brick world aesthetic',
    stylePrompt:
      'interlocking toy brick world, plastic brick construction, minifigure-like characters, block-built scenery, no logos or brand marks',
    thumbnail: '/images/video-styles/lego.webp',
  },
  {
    id: 'pixel-art',
    label: 'Pixel Art',
    description: 'Crisp retro pixel art visuals',
    stylePrompt:
      'pixel art, 16-bit game style, crisp square pixels, limited color palette, sprite-like detail, no blur',
    thumbnail: '/images/video-styles/pixel-art.webp',
  },
  {
    id: 'manga',
    label: 'Manga',
    description: 'Color manga illustration style',
    stylePrompt:
      'color manga illustration, dynamic ink linework, screentone-inspired shading, expressive character design, vibrant anime colors',
    thumbnail: '/images/video-styles/manga.webp',
  },
  {
    id: 'manga-black-white',
    label: 'Manga B&W',
    description: 'Black and white manga panel style',
    stylePrompt:
      'black and white manga illustration, ink linework, screentones, speed-line energy, high contrast monochrome panel',
    thumbnail: '/images/video-styles/manga-black-white.webp',
  },
  {
    id: 'kids-book',
    label: 'Kids Book',
    description: "Children's picture book illustration",
    stylePrompt:
      "children's picture book illustration, warm hand-painted texture, gentle rounded characters, whimsical scenery, soft colors",
    thumbnail: '/images/video-styles/kids-book.webp',
  },
  {
    id: 'minecraft',
    label: 'Minecraft',
    description: 'Blocky voxel world style',
    stylePrompt:
      'blocky voxel sandbox game style, cube-based terrain and characters, pixelated textures, no UI, no logos or brand marks',
    thumbnail: '/images/video-styles/minecraft.webp',
  },
  {
    id: 'claymation',
    label: 'Claymation',
    description: 'Handmade stop-motion clay look',
    stylePrompt:
      'claymation stop-motion style, handmade clay characters, tactile fingerprints, miniature set, soft studio lighting',
    thumbnail: '/images/video-styles/claymation.webp',
  },
  {
    id: 'cyberpunk',
    label: 'Cyberpunk',
    description: 'Neon futuristic city aesthetic',
    stylePrompt:
      'cyberpunk neon city, rainy reflective surfaces, magenta and cyan lights, futuristic atmosphere, dramatic contrast',
    thumbnail: '/images/video-styles/cyberpunk.webp',
  },
  {
    id: 'isometric',
    label: 'Isometric',
    description: 'Clean isometric diorama style',
    stylePrompt:
      'isometric illustration, three-quarter top-down view, clean geometry, miniature diorama composition, crisp shadows',
    thumbnail: '/images/video-styles/isometric.webp',
  },
  {
    id: 'film-noir',
    label: 'Film Noir',
    description: 'Moody black and white cinema',
    stylePrompt:
      'film noir black and white, high contrast chiaroscuro lighting, moody shadows, vintage 1940s cinematography',
    thumbnail: '/images/video-styles/film-noir.webp',
  },
  {
    id: 'charcoal-drawing',
    label: 'Charcoal Drawing',
    description: 'Expressive monochrome charcoal art',
    stylePrompt:
      'charcoal drawing, expressive smudged strokes, textured paper, dramatic tonal shading, monochrome fine-art look',
    thumbnail: '/images/video-styles/charcoal-drawing.webp',
  },
];

export type MediaType = 'ai-video' | 'ai-images';
export type WorkflowType =
  | 'image-story'
  | 'video-story'
  | 'motion-graphics'
  | 'stock-videos'
  | 'talking-video'
  /** User uploads photos/videos; the director assigns each scene to a
   * specific upload. Optionally runs i2v on each photo to add motion. */
  | 'your-own-media';

export const MEDIA_TYPES: Array<{
  id: MediaType;
  label: string;
  description: string;
}> = [
  {
    id: 'ai-video',
    label: 'AI Video',
    description: 'Generate unique video clips using AI',
  },
  {
    id: 'ai-images',
    label: 'AI Images',
    description: 'AI-generated images with motion effects',
  },
];

const WORKFLOW_PREVIEW_VIDEO =
  'https://media.easy-peasy.ai/assets/2f1117fe-a7f8-47eb-a8f8-d4fb6da0a02d.mp4';

export const WORKFLOW_TYPES: Array<{
  id: WorkflowType;
  label: string;
  description: string;
  previewVideo: string;
}> = [
  {
    id: 'image-story',
    label: 'Image story',
    description: 'Narration-synced AI images with motion',
    previewVideo:
      'https://media.easy-peasy.ai/assets/6ac31a34-e4ae-4d08-a5c6-df8a0aa8a080.mp4',
  },
  {
    id: 'video-story',
    label: 'Video clips',
    description: 'AI video shots, optionally featuring a chosen character',
    previewVideo:
      'https://media.easy-peasy.ai/assets/e0c9d213-124d-423c-a9a0-174bc3000da4.mp4',
  },
  {
    id: 'motion-graphics',
    label: 'Motion graphics',
    description: 'Text-led explainers with generated backgrounds',
    previewVideo: WORKFLOW_PREVIEW_VIDEO,
  },
  {
    id: 'stock-videos',
    label: 'Stock videos',
    description: 'Real stock footage matched to your script',
    previewVideo: WORKFLOW_PREVIEW_VIDEO,
  },
  {
    id: 'your-own-media',
    label: 'Your own media',
    description: 'Upload photos/videos; AI writes the narration around them',
    previewVideo:
      'https://media.easy-peasy.ai/assets/da511cbc-042b-405d-a2c5-421883e8522c.mp4',
  },
  // 'talking-video' is intentionally NOT exposed as a top-level workflow.
  // It's reachable from "Video clips" via the optional character toggle, and
  // is set as the workflowType under the hood when an actor is enabled.
];

export const WORKFLOW_TO_MEDIA_TYPE: Record<WorkflowType, MediaType> = {
  'image-story': 'ai-images',
  'video-story': 'ai-video',
  'motion-graphics': 'ai-images',
  'stock-videos': 'ai-video',
  'talking-video': 'ai-video',
  'your-own-media': 'ai-images',
};

// Video clip generators available for video-story / talking-video / stock-videos
// workflows. `creditsPerSecond` is the displayed cost — the backend reads
// it for credit accounting too. `id` is the wire format the API expects.
export type VideoModelId =
  | 'grok-imagine-video'
  | 'veo-3.1-fast'
  | 'veo-3.1'
  | 'gemini-omni-flash'
  | 'gemini-omni-flash-1.1'
  | 'kling-3.0-standard'
  | 'kling-3.0-pro'
  | 'happy-horse'
  | 'seedance-2.0'
  | 'seedance-2.0-fast'
  | 'seedance-2.0-mini'
  | 'seedance-1.5'
  | 'minimax-h3';

export interface VideoModel {
  id: VideoModelId;
  label: string;
  description: string;
  creditsPerSecond: number;
  /** Whether this model accepts an image as a first-frame reference (img2vid). */
  supportsImageToVideo: boolean;
  /** Whether this model can bake speech audio directly into the clip. */
  supportsBakedAudio: boolean;
  /**
   * Resolution the orchestrator requests for this model. Defaults to 720p when
   * omitted. Seedance models run at 480p to keep clip cost down, so their
   * `creditsPerSecond` already reflects the 480p rate (720p base × 0.5);
   * MiniMax H3 runs at its own 768P tier (2K base × 0.6).
   */
  resolution?: ResolutionType;
  /** Provider logo, matching the catalogue icons in `videoModels.ts`. */
  icon?: FC<React.SVGProps<SVGSVGElement>>;
}

// creditsPerSecond is kept in sync with the real provider rates in the model
// catalogue (`videoModels.ts`) — the video-dispatch credit calc reads these
// values to bill the user, so they MUST match the catalogue (at the resolution
// the orchestrator requests) or it over/under-charges relative to the
// standalone generator. Seedance entries carry the 480p-adjusted rate because
// the orchestrator runs them at 480p (see each model's `resolution`).
export const VIDEO_MODELS: VideoModel[] = [
  {
    id: 'grok-imagine-video',
    label: 'Grok',
    description: 'Fast, expressive, dialog-aware',
    creditsPerSecond: 2,
    supportsImageToVideo: true,
    supportsBakedAudio: true,
    icon: XAISvg,
  },
  {
    id: 'veo-3.1-fast',
    label: 'Veo 3.1 Fast',
    description: 'Google Veo, faster pass — strong photorealism',
    // Google's own 720p rate ($0.10/s ÷ $0.02/credit); matches
    // VEO_31_FAST_CREDITS_PER_SECOND in videoModels.ts.
    creditsPerSecond: 5,
    supportsImageToVideo: true,
    supportsBakedAudio: false,
    icon: GoogleSvg,
  },
  {
    id: 'veo-3.1',
    label: 'Veo 3.1',
    description: 'Google Veo, top quality — slowest, most cinematic',
    // Google's own 720p rate ($0.40/s ÷ $0.02/credit); matches
    // VEO_31_CREDITS_PER_SECOND in videoModels.ts.
    creditsPerSecond: 20,
    supportsImageToVideo: true,
    supportsBakedAudio: false,
    icon: GoogleSvg,
  },
  {
    id: 'gemini-omni-flash',
    label: 'Gemini Omni Flash',
    description: 'Google any-to-video — smart prompt following, native audio',
    // Matches the catalogue rate (creditsPerSecond: 6 in videoModels.ts).
    // Output is always 720p-class — the model has no resolution control, so
    // no `resolution` override here.
    creditsPerSecond: 6,
    supportsImageToVideo: true,
    supportsBakedAudio: true,
    icon: GoogleSvg,
  },
  {
    id: 'gemini-omni-flash-1.1',
    label: 'Gemini Omni Flash 1.1',
    description: 'Google any-to-video, newest — sharper motion, native audio',
    // 720p is the catalogue's base rate (creditsPerSecond: 6), and the
    // explicit `resolution` below pins the orchestrator to that tier so the
    // rate stays correct — 1.1's 1080p/4K multipliers would otherwise
    // undercharge here.
    creditsPerSecond: 6,
    resolution: '720p',
    supportsImageToVideo: true,
    supportsBakedAudio: true,
    icon: GoogleSvg,
  },
  {
    id: 'kling-3.0-standard',
    label: 'Kling 3.0 Standard',
    description: 'Kling baseline — solid motion, good prompt adherence',
    creditsPerSecond: 10,
    supportsImageToVideo: true,
    supportsBakedAudio: false,
    icon: KlingSvg,
  },
  {
    id: 'kling-3.0-pro',
    label: 'Kling 3.0 Pro',
    description: 'Kling Pro — sharper detail, smoother motion',
    creditsPerSecond: 15,
    supportsImageToVideo: true,
    supportsBakedAudio: false,
    icon: KlingSvg,
  },
  {
    id: 'happy-horse',
    label: 'Happy Horse',
    description: 'Stylised, playful — great for cartoon and claymation',
    creditsPerSecond: 7,
    supportsImageToVideo: true,
    supportsBakedAudio: false,
    icon: AlibabaSvg,
  },
  {
    id: 'seedance-2.0',
    label: 'Seedance 2.0',
    description: 'ByteDance Seedance — cinematic realism, native audio',
    // 480p rate: 720p base (12) × 0.5 from videoModels.ts resolutionCreditsMultiplier.
    creditsPerSecond: 6,
    supportsImageToVideo: true,
    supportsBakedAudio: true,
    resolution: '480p',
    icon: ByteDanceSvg,
  },
  {
    id: 'seedance-2.0-fast',
    label: 'Seedance 2.0 Fast',
    description: 'Seedance 2.0 at faster generation — cheaper, similar quality',
    // 480p rate: 720p base (6) × 0.5 from videoModels.ts resolutionCreditsMultiplier.
    creditsPerSecond: 3,
    supportsImageToVideo: true,
    supportsBakedAudio: true,
    resolution: '480p',
    icon: ByteDanceSvg,
  },
  {
    id: 'seedance-2.0-mini',
    label: 'Seedance 2.0 Mini',
    description: 'Smallest Seedance — fastest and cheapest, native audio',
    // 480p rate: 720p base (4) × 0.5 from videoModels.ts resolutionCreditsMultiplier.
    creditsPerSecond: 2,
    supportsImageToVideo: true,
    supportsBakedAudio: true,
    resolution: '480p',
    icon: ByteDanceSvg,
  },
  {
    id: 'seedance-1.5',
    label: 'Seedance 1.5 Pro',
    description: 'Older Seedance — cheapest realistic option',
    // 480p rate: 720p base (4) × 0.5 from videoModels.ts resolutionCreditsMultiplier.
    creditsPerSecond: 2,
    supportsImageToVideo: true,
    supportsBakedAudio: true,
    resolution: '480p',
    icon: ByteDanceSvg,
  },
  {
    id: 'minimax-h3',
    label: 'MiniMax H3',
    description: 'Cinematic, coherent motion with native audio',
    // 768P rate: 2K base (5) × 0.6 from videoModels.ts resolutionCreditsMultiplier.
    creditsPerSecond: 3,
    supportsImageToVideo: true,
    // H3 always renders native stereo audio from the prompt — there is no
    // parameter to turn it off.
    supportsBakedAudio: true,
    resolution: '768p',
    icon: HailuoSvg,
  },
];

// Credit multiplier applied when a clip is generated WITHOUT audio (e.g. the
// your-own-media i2v path, which always passes generateAudio:false because the
// editor has separate voiceover + music tracks). Skipping audio generation is
// cheaper provider-side, so users shouldn't pay the full audio-inclusive rate.
// NOTE: 0.8 (20% off) is an ESTIMATE — tune it against the real per-provider
// audio-off pricing when you have it. video-dispatch applies this in the
// credit calculation.
export const AUDIO_OFF_CREDIT_MULTIPLIER = 0.8;

export const DEFAULT_VIDEO_MODEL: VideoModelId = 'seedance-2.0-fast';

// Image generators used for image-based scenes and for first-frame i2i in
// video-based workflows. Wire format matches the `imageModel` string accepted
// by `generate_image` / `generate_image_to_image` in @/utils/functions.
export type ImageModelOption =
  | 'Nano Banana 2 Lite'
  | 'Nano Banana 2'
  | 'Nano Banana Pro'
  | 'OpenAI GPT Image 2 Low'
  | 'OpenAI GPT Image 2 Medium'
  | 'OpenAI GPT Image 2 High'
  | 'OpenAI GPT Image 2.5 Flare Low'
  | 'OpenAI GPT Image 2.5 Flare Medium'
  | 'OpenAI GPT Image 2.5 Flare High';

export interface ImageModel {
  id: ImageModelOption;
  label: string;
  description: string;
  /** Provider logo, matching the catalogue icons in `imageModels.ts`. */
  icon?: FC<React.SVGProps<SVGSVGElement>>;
}

export const IMAGE_MODELS: ImageModel[] = [
  {
    id: 'Nano Banana 2 Lite',
    label: 'Nano Banana 2 Lite',
    description:
      'Gemini 3.1 Flash-Lite Image — fastest, most cost-efficient (~4s)',
    icon: GoogleSvg,
  },
  {
    id: 'Nano Banana 2',
    label: 'Nano Banana 2',
    description: 'Gemini 3 Flash Image — fast, strong identity preservation',
    icon: GoogleSvg,
  },
  {
    id: 'Nano Banana Pro',
    label: 'Nano Banana Pro',
    description: 'Higher fidelity, better text rendering — slower',
    icon: GoogleSvg,
  },
  {
    id: 'OpenAI GPT Image 2.5 Flare Low',
    label: 'GPT Image 2.5 Flare Low',
    description: 'OpenAI GPT Image 2.5 Flare — fastest pass',
    icon: OpenAISvg,
  },
  {
    id: 'OpenAI GPT Image 2.5 Flare Medium',
    label: 'GPT Image 2.5 Flare Medium',
    description:
      'OpenAI GPT Image 2.5 Flare — balanced, up to 50% faster than GPT Image 2',
    icon: OpenAISvg,
  },
  {
    id: 'OpenAI GPT Image 2.5 Flare High',
    label: 'GPT Image 2.5 Flare High',
    description: 'OpenAI GPT Image 2.5 Flare — top quality',
    icon: OpenAISvg,
  },
  {
    id: 'OpenAI GPT Image 2 Low',
    label: 'GPT Image 2 Low',
    description: 'OpenAI GPT Image 2 — fastest pass',
    icon: OpenAISvg,
  },
  {
    id: 'OpenAI GPT Image 2 Medium',
    label: 'GPT Image 2 Medium',
    description: 'OpenAI GPT Image 2 — balanced',
    icon: OpenAISvg,
  },
  {
    id: 'OpenAI GPT Image 2 High',
    label: 'GPT Image 2 High',
    description: 'OpenAI GPT Image 2 — top quality, slowest',
    icon: OpenAISvg,
  },
];

export const DEFAULT_IMAGE_MODEL: ImageModelOption =
  'OpenAI GPT Image 2.5 Flare Medium';

// Catalogue display titles → slim ids. Media rows outside this pipeline
// (videos history imports, the editor's RightPanel, video-dispatch's own
// `videos` rows) store the catalogue TITLE — e.g. 'Veo 3.1 Image' — not the
// slim id. Keep in sync with SLIM_TO_TITLE in src/utils/video-dispatch.ts.
const VIDEO_TITLE_TO_SLIM: Record<string, VideoModelId> = {
  'Grok Imagine': 'grok-imagine-video',
  Grok: 'grok-imagine-video',
  'Veo 3.1': 'veo-3.1',
  'Veo 3.1 Fast': 'veo-3.1-fast',
  'Gemini Omni Flash': 'gemini-omni-flash',
  'Gemini Omni Flash 1.1': 'gemini-omni-flash-1.1',
  'Kling 3.0 Standard': 'kling-3.0-standard',
  'Kling 3.0 Pro': 'kling-3.0-pro',
  'Happy Horse': 'happy-horse',
  'Seedance 2.0': 'seedance-2.0',
  'Seedance 2.0 Fast': 'seedance-2.0-fast',
  'Seedance 2.0 Mini': 'seedance-2.0-mini',
  'Seedance 1.5 Pro': 'seedance-1.5',
  'MiniMax H3': 'minimax-h3',
};

/**
 * Resolve a stored model string (slim id, catalogue title, or an i2v/ref
 * variant title like 'Kling 3.0 Standard Image') to a slim VideoModelId.
 * Returns null for models the regeneration flow doesn't support (e.g. Sora).
 */
export function resolveVideoModelId(
  value?: string | null,
): VideoModelId | null {
  if (!value) return null;
  const v = value.trim();
  if (VIDEO_MODELS.some((m) => m.id === v)) return v as VideoModelId;
  if (VIDEO_TITLE_TO_SLIM[v]) return VIDEO_TITLE_TO_SLIM[v];
  const base = v.replace(/\s+(Image|Reference to Video|Reference)$/i, '');
  return VIDEO_TITLE_TO_SLIM[base] ?? null;
}

/** Resolve a stored model string to a known ImageModelOption (or null). */
export function resolveImageModelId(
  value?: string | null,
): ImageModelOption | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  const hit = IMAGE_MODELS.find(
    (m) => m.id.toLowerCase() === v || m.label.toLowerCase() === v,
  );
  return hit ? hit.id : null;
}

export const MUSIC_PRESETS = [
  {
    id: 'upbeat-corporate',
    genre: 'pop',
    mood: 'uplifting',
    label: 'Upbeat Corporate',
  },
  { id: 'calm-ambient', genre: 'ambient', mood: 'calm', label: 'Calm Ambient' },
  {
    id: 'epic-cinematic',
    genre: 'classical',
    mood: 'epic',
    label: 'Epic Cinematic',
  },
  {
    id: 'energetic-electronic',
    genre: 'electronic',
    mood: 'energetic',
    label: 'Energetic Electronic',
  },
  {
    id: 'happy-acoustic',
    genre: 'pop',
    mood: 'happy',
    label: 'Happy Acoustic',
  },
  {
    id: 'mysterious-dark',
    genre: 'ambient',
    mood: 'mysterious',
    label: 'Mysterious Dark',
  },
  {
    id: 'dramatic-orchestral',
    genre: 'classical',
    mood: 'dramatic',
    label: 'Dramatic Orchestral',
  },
  { id: 'lofi-chill', genre: 'lo-fi', mood: 'calm', label: 'Lo-Fi Chill' },
];
